// Lecture des données d'un dossier selon le schéma de la version de sa démarche : sections et
// champs dans l'ordre du formulaire, valeurs rendues selon leur type (choix par leur libellé,
// adresse composée, tableau en lignes), champs hors schéma listés à part sans être masqués.
import { LIBELLES_TYPES_CHAMP, tousLesChamps, type Champ, type DonneesFormulaire, type Formulaire } from '@cc/contrats';
import styles from './formulaire.module.css';

function estVide(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

function texteOption(champ: Pick<Champ, 'options'>, v: unknown): string {
  return champ.options?.find((o) => o.valeur === v)?.libelle ?? String(v);
}

/** Valeur lisible d'un champ typé ; les objets inconnus sont rendus en JSON compact. */
export function valeurLisible(champ: Champ | undefined, v: unknown): string {
  if (estVide(v)) return 'non renseigné';
  if (!champ) return typeof v === 'object' ? JSON.stringify(v) : String(v);
  switch (champ.type) {
    case 'oui_non':
      return v === true ? 'Oui' : v === false ? 'Non' : String(v);
    case 'choix_unique':
      return texteOption(champ, v);
    case 'choix_multiple':
      return Array.isArray(v) ? v.map((x) => texteOption(champ, x)).join(', ') : String(v);
    case 'montant':
      return typeof v === 'number' ? `${v.toLocaleString('fr-FR')} francs CFA` : String(v);
    case 'nombre':
      return typeof v === 'number' ? v.toLocaleString('fr-FR') : String(v);
    case 'date':
      return typeof v === 'string' ? new Date(`${v}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : String(v);
    case 'adresse': {
      const a = v as Record<string, unknown>;
      return [a.voie, a.quartier, a.ville, a.complement].filter(Boolean).join(', ');
    }
    case 'localisation': {
      const p = v as Record<string, unknown>;
      return `latitude ${String(p.latitude)}, longitude ${String(p.longitude)}`;
    }
    case 'fichier':
      return `pièce ${String(v).slice(0, 8)}`;
    case 'tableau':
      return Array.isArray(v) ? `${v.length} ligne${v.length > 1 ? 's' : ''}` : String(v);
    default:
      return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
}

function Tableau({ champ, lignes }: { champ: Champ; lignes: Record<string, unknown>[] }) {
  const colonnes = champ.colonnes ?? [];
  return (
    <table className={styles.lecture}>
      <caption className="cc-masque">{champ.libelle}</caption>
      <thead>
        <tr>
          {colonnes.map((k) => (
            <th key={k.code} scope="col">
              {k.libelle}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.map((l, i) => (
          <tr key={i}>
            {colonnes.map((k) => (
              <td key={k.code}>{valeurLisible({ code: k.code, type: k.type, libelle: k.libelle, obligatoire: k.obligatoire, options: k.options } as Champ, l[k.code])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface ProprietesAffichageDonnees {
  /** Schéma de la version ; absent quand la version n'est pas au format de l'éditeur. */
  formulaire: Formulaire | null;
  donnees: DonneesFormulaire;
  /** Noms des pièces par identifiant de document (champs fichier). */
  pieces?: Record<string, string>;
}

export function AffichageDonnees({ formulaire, donnees, pieces = {} }: ProprietesAffichageDonnees) {
  const connus = new Set(formulaire ? tousLesChamps(formulaire).map((x) => x.champ.code) : []);
  const horsSchema = Object.entries(donnees).filter(([code]) => !connus.has(code));
  const sections = (formulaire?.sections ?? []).filter((s) => s.champs.some((c) => !estVide(donnees[c.code])));
  if (sections.length === 0 && horsSchema.length === 0) return <p className={styles.vide}>Aucune donnée de formulaire enregistrée.</p>;
  return (
    <div className={styles.formulaire}>
      {sections.map((s) => (
        <section key={s.code} className={styles.section}>
          <h3>{s.titre}</h3>
          <dl className={styles.donnees}>
            {s.champs
              .filter((c) => !estVide(donnees[c.code]))
              .map((c) => (
                <div key={c.code} className={styles.donnee}>
                  <dt>
                    {c.libelle} <span className={styles.type}>{LIBELLES_TYPES_CHAMP[c.type]}</span>
                  </dt>
                  <dd>
                    {c.type === 'tableau' && Array.isArray(donnees[c.code]) ? (
                      <Tableau champ={c} lignes={donnees[c.code] as Record<string, unknown>[]} />
                    ) : c.type === 'fichier' && typeof donnees[c.code] === 'string' ? (
                      (pieces[donnees[c.code] as string] ?? valeurLisible(c, donnees[c.code]))
                    ) : (
                      valeurLisible(c, donnees[c.code])
                    )}
                  </dd>
                </div>
              ))}
          </dl>
        </section>
      ))}
      {horsSchema.length > 0 ? (
        <section className={styles.section}>
          <h3>{formulaire ? 'Données hors du schéma de la version' : 'Données saisies'}</h3>
          <dl className={styles.donnees}>
            {horsSchema.map(([code, v]) => (
              <div key={code} className={styles.donnee}>
                <dt>{code}</dt>
                <dd>{valeurLisible(undefined, v)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </div>
  );
}
